import { Account } from '@/models/Account';
import { createContext, PropsWithChildren, useCallback, useMemo, useState } from 'react';

export interface AccountContextValue {
  account: Account | undefined;
  setAccount: (account: Account) => void;
  isAccountSelectionOpen: boolean;
  setAccountSelectionOpen: (isOpen: boolean) => void;
}

/**
 * The props of the provider component
 */
interface ProviderProps extends PropsWithChildren {
  /**
   * The account ID to set as the default account
   * @type {string | undefined}
   */
  accountId?: string;
}

export const AccountContext = createContext<AccountContextValue>({
  account: undefined,
  isAccountSelectionOpen: false,
  setAccount: () => {},
  setAccountSelectionOpen: () => {},
});

const AccountProvider = (props: ProviderProps) => {
  const { children, accountId } = props;
  const [accountSelected, setAccountSelected] = useState<Account | undefined>(
    accountId
      ? {
          id: accountId,
          name: '',
        }
      : undefined,
  );
  const [isAccountSelectionOpen, setIsAccountSelectionOpen] = useState<boolean>(false);

  const handleAccountSelected = useCallback(
    (account: Account) => {
      setAccountSelected(account);
      setIsAccountSelectionOpen(false);
    },
    [setAccountSelected, setIsAccountSelectionOpen],
  );

  const value = useMemo(
    () => ({
      account: accountSelected,
      setAccount: handleAccountSelected,
      isAccountSelectionOpen,
      setAccountSelectionOpen: setIsAccountSelectionOpen,
    }),
    [accountSelected, handleAccountSelected, isAccountSelectionOpen, setIsAccountSelectionOpen],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};
export default AccountProvider;
